import { Component, useContext } from "react";
import RadarGraph from "./RadarGraph";
import BarGraph from "./BarGraph";
import "./PageCharts.css";
import { FormattedMessage } from "react-intl";
import messages from "../lang/preferences";
import HelpModal from "./HelpModal";
import { getTopExtremeTraitsUpperThird } from "../utils/traitsOrderMedian";
import ScopeReportWithAttitudesIntroPage from "./ScopeReportWithAttitudesIntroPage";
import ppeFullLogo from '../ressources/logo-with-text-green.svg';
import { LanguageContext } from '../contexts/LanguageContext';
import AttitudesPage from '../components/AttitudesPage';
import AttitudesPageTwo from '../components/AttitudesPageTwo';


const PAGE_NUMBERS = [3, 0, 0, 0, 5, 8, 10]; // adjusted for the attitudes page

const formatDateForDisplay = (value, locale, options) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(locale, options);
};

function getTraitPerentile(trait, pageNumber = 0) {
  let traitKey = trait.en === "Self assurance" ? "Selfassurance" : trait.en;
  traitKey = traitKey.toUpperCase().replace(/ /g, "_");

  const label = trait.extremeLabel === "lowerThird" ? "LOW" : "HIGH";
  const percentile = trait.extremeLabel === "lowerThird" ? "25" : "100";

  if(pageNumber === 1 && trait.extremeLabel === "lowerThird") {
    trait.extremeLabel = "upperThird";
    //console.log("Special case triggered",);
  }

  const prefix = (pageNumber === 6 || pageNumber === 5)
    ? `SCOPE_REPORT_${traitKey}`
    : `SCOPE_REPORT_${traitKey}_AXIS_${label}`;
  const percentilePrefix = `SCOPE_REPORT_${traitKey}_PERCENTILE_${percentile}`;
  const percentileTitle = `SCOPE_REPORT_PERCENTILE_${percentile}_TITLE`;

  // Only include optimize/motivation if not page 6
  if (pageNumber === 6 || pageNumber === 5) {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentileTitle}`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      collaboration: `${percentilePrefix}_COLLABORATION`,
      management: `${percentilePrefix}_MANAGEMENT`,
      recognition: `${percentilePrefix}_RECOGNITION`,
      teamKnow: `${percentilePrefix}_TEAM_KNOW`,
      teamKnowLabel: `SCOPE_REPORT_TEAM_KNOW_LABEL`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  } else {
    return {
      title: `${prefix}`,
      description: `${prefix}_DESC`,
      percentile: `${percentilePrefix}_TITLE`,
      strengthsIntro: `${percentilePrefix}_STRENGTHS_INTRO`,
      strengths1: `${percentilePrefix}_STRENGTHS_1`,
      strengths2: `${percentilePrefix}_STRENGTHS_2`,
      strengths3: `${percentilePrefix}_STRENGTHS_3`,
      developmentIntro: `${percentilePrefix}_DEVELOPMENT_INTRO`,
      development1: `${percentilePrefix}_DEVELOPMENT_1`,
      development2: `${percentilePrefix}_DEVELOPMENT_2`,
      development3: `${percentilePrefix}_DEVELOPMENT_3`,
      optimize4me: `SCOPE_REPORT_OPTIMIZE_FOR_ME_LABEL`,
      leversMotivation: `SCOPE_REPORT_LEVERS_OF_MOTIVATION_LABEL`,
      optimize: `${percentilePrefix}_OPTIMIZE`,
      motivation: `${percentilePrefix}_MOTIVATION`,
      teamAdvice: `${percentilePrefix}_TEAM_ADVICE`,
      teamAdviceLabel: `SCOPE_REPORT_TEAM_ADVICE_LABEL`,
    };
  }
}

// Helper to safely render FormattedMessage
function SafeFormattedMessage({ msgKey }) {
  const msg = messages[msgKey];
  const valid = msg && typeof msg.id === "string" && msg.id.length > 0;
  if (valid) {
    return <FormattedMessage {...msg} />;
  }
  return msgKey || "";
}

const getDurationLocaleConfig = (locale) => {
  const normalized = String(locale || 'en').toLowerCase();

  if (normalized.startsWith('ja')) {
    return { h: '時間', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh-tw') || normalized.startsWith('zh-hk') || normalized.includes('hant')) {
    return { h: '小時', m: '分', s: '秒', compact: true };
  }

  if (normalized.startsWith('zh')) {
    return { h: '小时', m: '分', s: '秒', compact: true };
  }

  return { h: 'h', m: 'm', s: 's', compact: false };
};

const formatDuration = (duration, locale) => {
  const unitCfg = getDurationLocaleConfig(locale);

  const formatParts = (hours, minutes, seconds) => {
    if (unitCfg.compact) {
      return `${hours}${unitCfg.h}${minutes}${unitCfg.m}${seconds}${unitCfg.s}`;
    }
    return `${hours}${unitCfg.h} ${minutes}${unitCfg.m} ${seconds}${unitCfg.s}`;
  };

  const fallback = formatParts(0, 33, 33);

  const buildDuration = (hours, minutes, seconds) => {
    const h = Number(hours);
    const m = Number(minutes);
    const s = Number(seconds);

    if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) {
      return fallback;
    }

    if (h < 0 || m < 0 || s < 0) {
      return fallback;
    }

    return formatParts(Math.floor(h), Math.floor(m), Math.floor(s));
  };

  try {
    if (duration === null || typeof duration === 'undefined' || duration === '' || duration === 'null') {
      return fallback;
    }

    if (typeof duration === 'object' && duration !== null && 'hours' in duration) {
      return buildDuration(duration.hours, duration.minutes, duration.seconds);
    }
    
    if (typeof duration === 'string') {
        const compactMatch = duration.match(/^(\d+)h\s*(\d+)m\s*(\d+)s$/i);
        if (compactMatch) {
          return buildDuration(compactMatch[1], compactMatch[2], compactMatch[3]);
        }

        const genericThreeNumbers = duration.match(/(\d+)\D+(\d+)\D+(\d+)/);
        if (genericThreeNumbers) {
          return buildDuration(genericThreeNumbers[1], genericThreeNumbers[2], genericThreeNumbers[3]);
        }

        const jsonString = duration
          .replace(/(\w+):/g, '"$1":')  // Add quotes around property names
          .replace(/'/g, '"');          // Replace single quotes with double quotes
        
        try {
          const parsed = JSON.parse(jsonString);
          return buildDuration(parsed.hours, parsed.minutes, parsed.seconds);
        } catch (e2) {
          //console.log("Failed to convert to JSON");
        }
    }
    
    return fallback;
  } catch (e) {
    //console.error("ERROR in formatDuration:", e);
    return fallback;
  }
};


function renderTraitDetailsList({
  data,
  type,
  filterIdx = 2,
  getTopTraits = getTopExtremeTraitsUpperThird,
  liClassName = "list-counter-marker",
  pageNumber = 1 
})
{
      const topTraits = getTopTraits(data, type);
      // If any selected trait was filled from the middle third, decide whether it should
      // map to LOWER or UPPER texts based on which half of the middle third it sits in.
      // If it's in the lower half of the middleThird, pivot to lowerThird texts.
      try {
        const getUserScore = trait => typeof trait.user === 'number' ? trait.user : parseFloat(trait.user);
        topTraits.forEach(trait => {
          if (!trait || trait.extremeLabel !== 'middleThird') return;
          const userScore = getUserScore(trait);
          let getMiddleUpper, getMiddleBottom;
          if (type === 'bar') {
            const mt = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
            const lt = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
            getMiddleUpper = mt + lt;
            getMiddleBottom = lt;
          } else {
            getMiddleUpper = typeof trait.middleThird === 'number' ? trait.middleThird : parseFloat(trait.middleThird);
            getMiddleBottom = typeof trait.lowerThird === 'number' ? trait.lowerThird : parseFloat(trait.lowerThird);
          }
          const middleCenter = (getMiddleUpper + getMiddleBottom) / 2;
          if (!isNaN(userScore) && userScore <= middleCenter) {
            trait.extremeLabel = 'lowerThird';
          } else {
            trait.extremeLabel = 'upperThird';
          }
        });
      } catch (e) {
        // defensive: if anything goes wrong, leave labels as-is
        console.warn('Failed to canonicalize middleThird labels', e);
      }
      return topTraits.map((trait, idx) => {
        if (typeof filterIdx === "function" ? !filterIdx(idx, trait) : idx !== filterIdx) return null;
        const traitDetails = getTraitPerentile(trait, pageNumber);
        const extremeKey = trait.extremeLabel ? trait.extremeLabel.toUpperCase() : "";
        let getDetails = traitDetails.description;
        let managerDetails = false;
        //console.log("traitDetails", traitDetails, "extremeKey", extremeKey, "pageNumber", pageNumber);
        if(pageNumber === 6){
          if(extremeKey === "LOWERTHIRD") {
            
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
            //console.log("traitDetails", getDetails, trait.en.toUpperCase().replace(/ /g, "_"));
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
          }
          else{
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
          }
          
        }
            if(pageNumber === 5){
          if(extremeKey === "LOWERTHIRD") {
            
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC_LOW`;
            //console.log("traitDetails", getDetails, trait.en.toUpperCase().replace(/ /g, "_"));
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC_LOW`;
          }
          else{
            getDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_DESC`;
            managerDetails = `SCOPE_REPORT_${trait.en.toUpperCase().replace(/ /g, "_")}_MANAGER_DESC`;
          }
          
        }
        if(pageNumber === 1 ) { //console.log("PAGE 1 traitDetails",traitDetails, "extremeKey", extremeKey);
          }
        return (
                  <li key={idx + "_traits"} className={liClassName}>
                    <b className="font-18">
                      <SafeFormattedMessage msgKey={traitDetails.title} />
                    </b>  - {(
                      <SafeFormattedMessage msgKey={extremeKey} />
                    )}
                    {/*typeof trait.user === "number" ? trait.user : parseFloat(trait.user)*/}
                    <span className="trait-details-box">
                      <br />
                      <SafeFormattedMessage msgKey={getDetails} />
                      <br />
                      { (pageNumber === 5 || pageNumber === 6) && managerDetails && <div className="trait-details-list-box"><b><SafeFormattedMessage msgKey={'SCOPE_REPORT_MANAGER_SUMMARY_LABEL'} /></b> <SafeFormattedMessage msgKey={managerDetails} /></div>}
                      {/* HIDE percentile <span className="percentile-subtitle"><i><SafeFormattedMessage msgKey={traitDetails.percentile} /></i></span>*/}
                      <span className="trait-details-list-box">
                        {/* Strengths List */}
                        <span>
                          <span>
                            <SafeFormattedMessage msgKey={traitDetails.strengthsIntro} />
                          </span>
                          <ul className="trait-strengths-list" style={{ margin: 0, paddingLeft: 24 }}>
                            {[traitDetails.strengths1, traitDetails.strengths2, traitDetails.strengths3]
                              .map((key, idx) => {
                                const msg = messages[key];
                                if (!msg || !msg.defaultMessage) return null;
                                return (
                                  <li className="trait-strengths-list-item" key={idx}>
                                    <SafeFormattedMessage msgKey={key} />
                                  </li>
                                );
                              })}
                          </ul>
                        </span>
                      </span>
                      <span className="trait-details-list-box">
                        <span>
                          <span>
                            <SafeFormattedMessage msgKey={traitDetails.developmentIntro} />
                          </span>
                          <ul className="trait-development-list" style={{ margin: 0, paddingLeft: 24 }}>
                            {[traitDetails.development1, traitDetails.development2, traitDetails.development3]
                              .map((key, idx) => {
                                const msg = messages[key];
                                if (!msg || !msg.defaultMessage) return null;
                                return (
                                  <li className="trait-development-list-item" key={idx}>
                                    <SafeFormattedMessage msgKey={key} />
                                  </li>
                                );
                              })}
                          </ul>
                        </span>
                      </span>
                      {/* Optimize */}
                      {pageNumber !== 6 && (
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.optimize4me}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.optimize}
                            />
                          </span>
                        </span>
                      )}
                      {/* Motivation */}
                      {pageNumber !== 6 && (
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.leversMotivation}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.motivation}
                            />
                          </span>
                        </span>
                      )}

                      {/* Team Advice personality */}
                      {pageNumber === 1 && (
                        <span className="trait-details-list-box">
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                        </span>
                      )}
                      {/* For pageNumber 5, show the extra fields */}
                      {(pageNumber === 5) && (
                        <>
                          {/* For Collaboration */}
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.collaboration}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.management}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.recognition}
                              />
                            </span>
                          </span>
                        {/* Team Advice */}
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                          <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamKnowLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamKnow}
                            />
                          </span>
                        </span>
                        </>
                      )}

                      {/* For page number 6, show the extra fields */}
                      {(pageNumber === 6) && (
                        <>
                        {/* Team Advice */}
                        <span className="trait-details-list-box">
                          <span>
                            <b><SafeFormattedMessage
                              msgKey={traitDetails.teamAdviceLabel}
                            /></b>
                            <SafeFormattedMessage
                              msgKey={traitDetails.teamAdvice}
                            />
                          </span>
                        </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_COLLABORATION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.collaboration}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_MANAGEMENT_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.management}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_RECOGNITION_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.recognition}
                              />
                            </span>
                          </span>
                          <span className="trait-details-list-box">
                            <span>
                              <b><SafeFormattedMessage
                                msgKey={"SCOPE_REPORT_ESSENTIAL_FOR_ME_LABEL"}
                              /></b>
                              <SafeFormattedMessage
                                msgKey={traitDetails.teamKnow}
                              />
                            </span>
                          </span>
                        </>
                      )}
                    </span>
                  </li>
        );
      });
  }

function PageChartsWrapper(props) {
  // Use the candidate's language, not the global context
  const { locale } = useContext(LanguageContext);
  /*console.log("LOCALE:::::", locale);*/
  return <PageChartsWithContext {...props} locale={props.userLanguage || locale} localeDisplay={locale} />;
}

export class PageChartsWithContext extends Component {
  state = {
    premium: true,
    loading: false,
    widthParentBar: "90%",
    heightParentBar: "50%",
    widthParentRadar: "90%",
    heightParentRadar: "50%",
    many: "top",
    graphs: [],
  };

  _displayGraph = (type, data, cp = false) => {
    switch (type) {
      case "radar":
        return (
          <div className="PageCharts-parentRadar">
            {data && data.length === 3 && 
              <div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box expressivity-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS" /></b>
                    <div className="expressivity-desc emotionality-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EXPRESSIVENESS_DESC" />
                    </div>
                  </div>
                </div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box regulation-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION" /></b>
                    <div className="emotionality-desc regulation-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTIVEREGULATION_DESC" />
                    </div>
                  </div>
                </div>
                <div className="action-orientation-box">
                  <div className="action-orientation-box resilience-box">
                    <b><SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE" /></b>
                    <div className="emotionality-desc resilience-desc">
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_EMOTIONALRESILIENCE_DESC" />
                    </div>
                  </div>
                </div>
              </div>
            }
           <RadarGraph data={data} />
          </div>
        );
      case "bar":
        return (     
          <div className={`PageCharts-parentBar${cp ? '-horizontal' : ''}`}>
            { /*console.log("DATA::::", data[8])*/}
            { !cp && data?.length === 9 && data[8]?.fr=== "Style cognitif" && 
              <div className="scope-personality-table-container">
                  <table className="scope-table-fullwidth scope-personality-table-spacing">
                    <tbody>
                      {/* Extraversion */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXTRAVERSION_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Excitement Seek */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_EXCITEMENT_SEEKING_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Impulsivity */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_IMPULSIVITY_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Pessimism */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PESSIMISM_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Self-Assurance */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_SELFASSURANCE_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Dominance */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col  col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_DOMINANCE_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Aggressiveness */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_AGGRESSIVENESS_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                      {/* Conscientiousness */}
                      <tr>
                        <td className="scope-table-col">
                          <div className="scope-table-title-left">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_LOW_DESC" />
                        </td>
                        <td className="col-middle">
                        </td>
                        <td className="scope-table-col col-right">
                          <div className="scope-table-title-right">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH" />
                          </div>
                          <br />
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_CONSCIENTIOUSNESS_AXIS_HIGH_DESC" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
              </div>
            }
              <BarGraph data={data} cp={cp} />
          </div>
        );
      default:
        return null;
    }
  };

  componentDidMount() {
    if(this.props.timeTaken!=="null"){ /*console.log("TIME TAKEN");*/ }else{ /*console.log("NULL TIME TAKEN");*/ }
    /*console.log("PageCharts user lang", this.props.userLanguage);*/
    
    // Handle both array and object formats for dataForGraphs
    const graphsData = Array.isArray(this.props.dataForGraphs) 
      ? this.props.dataForGraphs 
      : this.props.dataForGraphs?.dataForGraph || [];
    
    this.setState({
      graphs: graphsData,
      code: this.props.code,
      lang: this.props.userLanguage,
    });
  }

render() {
  return (
    <div className="PageCharts">
      {this.state.graphs.map((graph, key) => {
        const p = key + 1;
        // Use the array for page numbers
        const keyPage = PAGE_NUMBERS[key] || 15;

          //console.log("page p", p, keyPage);
          return (
            <div key={key}>
              {/* Cover page */}
              {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="report-alt-page">
                    <img
                      alt="ppe-logo"
                      className="report-main-logo"
                      src={ppeFullLogo}
                      style={{ position: "absolute", left: "40px", top: "40px", width: "220px" }}
                    />
                    <div className="report-alt-page-candidate">
                      <div className="candidate-separator report-title attitudes-title">
                        <b><SafeFormattedMessage msgKey="REPORT_TITLE" /> </b>
                        <br />
                        <b><SafeFormattedMessage msgKey="REPORT_ATTITUDES_SUBTITLE" /></b>
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="CANDIDATE" /></b> {this.state.code}
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>{" "}
                        {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                      </div>
                      <div className="candidate-separator">
                        <b><SafeFormattedMessage msgKey="TEST_TIME_TAKEN" /></b>{" "}
                        {formatDuration(this.props.timeTaken, this.props.localeDisplay || this.props.currentLanguage)}
                      </div>
                    </div>
                  </div>
                  <div className="pdf-footer"></div>
                </div>
              )}

              {/* Intro page */}
              {p === 1 && (
                <ScopeReportWithAttitudesIntroPage
                  messages={messages}
                  email={this.state.code}
                  datePassed={this.props.datePassed}
                  pageNumber={p + 1}
                  pageNumbers={PAGE_NUMBERS}
                  logo={ppeFullLogo}/>
              )}

              {/* Help modal */}
              <HelpModal
                dataTitles={graph.dataTitles}
                dataDescriptions={graph.dataDescriptions}
              />

              {/* Main chart */}
              { (p === 1 || p === 5 || p === 6) && (<div className="PageCharts-chart">
                <div className="header-logo-box">
                  <img
                    alt="ppe-logo"
                    className="header-logo-img"
                    src={ppeFullLogo}
                  />
                </div>
                <div className={this.props.askedfor === "admin" ? "admin-charts" : "user-charts"}>
                  <div className="PageCharts-head">
                    <div className="PageCharts-title">
                      <h1>
                        <SafeFormattedMessage msgKey={graph.title} />
                      </h1>
                      <p>
                        <SafeFormattedMessage msgKey={graph.description} />
                      </p>
                    </div>
                  </div>
                  {graph.type !== "radar" && (
                    <div className="PageCharts-svg PageCharts-svg-left">
                      {this._displayGraph(graph.type, graph.data)}
                    </div>
                  )}
                  {graph.type === "radar" && (
                    <div className="PageCharts-svg">
                      {this._displayGraph(graph.type, graph.data)}
                    </div>
                  )}
                </div>
                <div className="pdf-footer">
                  <SafeFormattedMessage msgKey="PAGE" /> {keyPage}{" "}
                  <div className="pdf-footer-separator">
                    <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                  </div>{" "}
                  {this.state.code}
                  <div className="pdf-footer-separator">
                    <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                  </div>
                </div>
              </div>
              )}
              
              {/* Personality traits page 1 */}
              {p === 1 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Personality traits page 2 */}
              { /* p === 1 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - AND ME<SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              ) */}

              {/* Workplace Definitions page */}
              {p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_VALUEPREF_TITLE" />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="DEFINITIONS" /></span>
                    </h1>
                    <table className="scope-table-fullwidth">
                      <tbody>
                        {/* Plaisir au travail */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_ENJOY_WORK" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_ENJOY_WORK_DESC" />
                          </td>
                        </tr>
                        {/* Leadership */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_LEADERSHIP" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_LEADERSHIP_DESC" />
                          </td>
                        </tr>
                        {/* SÃ©curitÃ© de l'emploi */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_JOB_SECURITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_JOB_SECURITY_DESC" />
                          </td>
                        </tr>
                        {/* Stimulation intellectuelle */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_INTELLECTUAL_STIMULATION" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_INTELLECTUAL_STIMULATION_DESC" />
                          </td>
                        </tr>
                        {/* Salaire */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SALARY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SALARY_DESC" />
                          </td>
                        </tr>
                        {/* TÃ¢ches variÃ©es */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_TASK_VARIABILITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_TASK_VARIABILITY_DESC" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Workplace Values top 3 traits page 1 */}
              {p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Workplace Values top 3 traits page 2 */}
              {/* p === 5 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 3}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              ) */}

              {/* Value hierarchy Definitions page */}
              {p === 6 && (
                <div className="PageCharts-chart">
                <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPEREPORT_VALUEPREFERENCES2" />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="DEFINITIONS" /></span>
                    </h1>
                    <table className="scope-table-fullwidth">
                      <tbody>
                        {/* Plaisir au travail */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_POWER" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_POWER_DESC" />
                          </td>
                        </tr>
                        {/* Leadership */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_SUCCESS" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_SUCCESS_DESC" />
                          </td>
                        </tr>
                        {/* SÃ©curitÃ© de l'emploi */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_MORALITY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_MORALITY_DESC" />
                          </td>
                        </tr>
                        {/* Stimulation intellectuelle */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_HONOR" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_HONOR_DESC" />
                          </td>
                        </tr>
                        {/* Salaire */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTION" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_AFFECTION_DESC" />
                          </td>
                        </tr>
                        {/* TÃ¢ches variÃ©es */}
                        <tr>
                          <td className="scope-table-col">
                            <b>
                              <SafeFormattedMessage msgKey="SCOPE_REPORT_HARMONY" />
                            </b>
                          </td>
                        </tr>
                        <tr>
                          <td className="scope-table-col">
                            <SafeFormattedMessage msgKey="SCOPE_REPORT_HARMONY_DESC" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 1}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Value hierarchy top 3 traits page 1 */}
              {p === 6 && (
                <div className="PageCharts-chart">
                <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: idx => idx <= 1,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 2}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Value hierarchy top 3 traits page 2 */}
              {/*p === 6 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey={graph.title} />
                      <span className="inline font-16"> - <SafeFormattedMessage msgKey="SCOPE_REPORT_TOP_THREE" /></span>
                    </h1>
                    <ol className="list-counter" start="3">
                      {renderTraitDetailsList({
                        data: graph.data,
                        type: graph.type,
                        filterIdx: 2,
                        pageNumber: p
                      })}
                    </ol>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 3}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              ) */}

              {/* Attitudes page (embedded) */}
              {p === 6 && (
                <AttitudesPage
                  user={{
                    // provide minimal user object expected by AttitudesPage
                    code: this.props.code,
                    email: this.props.email,
                    results: this.props.dataForGraphs?.results || this.props.dataForGraphs,
                    date: this.props.datePassed
                  }}
                  pageNumber={keyPage + 3}
                  embedded={true}
                  localeDisplay={this.props.localeDisplay}
                  onBack={() => window.history.back()}
                />
              )}
              
               {/* Attitudes page two (embedded) */}
              {p === 6 && (
                <AttitudesPageTwo
                  user={{
                    // provide minimal user object expected by AttitudesPage
                    code: this.props.code,
                    email: this.props.email,
                    results: this.props.dataForGraphs?.results || this.props.dataForGraphs,
                    date: this.props.datePassed
                  }}
                  pageNumber={keyPage + 4}
                  embedded={true}
                  localeDisplay={this.props.localeDisplay}
                  onBack={() => window.history.back()}
                />
              )}

              {/* Personal Notes Page - LAST PAGE */}
              {p === 6 && (
                <div className="PageCharts-chart">
                  <div className="header-logo-box">
                      <img
                        alt="ppe-logo"
                        className="header-logo-img"
                        src={ppeFullLogo}
                      />
                  </div>
                  <div className="header-logo-box">
                    <img
                      alt="ppe-logo"
                      className="header-logo-img"
                      src={ppeFullLogo}
                    />
                  </div>
                  <div className="report-alt-page">
                    <h1>
                      <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_TITLE" />
                    </h1>
                    <div className="personal-notes-section">
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_STRENGTHS_CONTEXTS_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_DEVELOPMENT_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_OBSTACLES_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line personal-notes-line-space"></div>
                      </div>
                      <div className="personal-notes-block">
                        <b><SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION" /></b>
                        <div className="personal-notes-italic">
                          <SafeFormattedMessage msgKey="SCOPE_REPORT_PERSONAL_NOTES_MOTIVATION_EXAMPLE" />
                        </div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                        <div className="personal-notes-line"></div>
                      </div>
                    </div>
                  </div>
                  <div className="pdf-footer">
                    <SafeFormattedMessage msgKey="PAGE" /> {keyPage + 5}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="CANDIDATE" /></b>
                    </div>{" "}
                    {this.state.code}
                    <div className="pdf-footer-separator">
                      <b><SafeFormattedMessage msgKey="TEST_DATE" /></b>  {formatDateForDisplay(this.props.datePassed, 
                          this.props.localeDisplay || undefined,
                          { day: "2-digit", month: "long", year: "numeric" }
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

export default PageChartsWrapper;
